import { useState, useRef, useEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { BASE_SHEET_WIDTH } from '@i-ark/constants';
import { throttleToAnimationFrame } from '@utils/common';
import { assertRefObject, CSSRefTransition } from '@utils/react';
import { PrintContextProvider } from '../print-context.js';
import { sheetCls, TRANSITION_DURATION, transitionCls } from './Sheet.style.js';
import { jsx } from 'react/jsx-runtime';
import { ScaleContext } from './hooks/useScale.js';
import { RenderComponent } from './components/RenderComponent.js';

function Sheet({
  page,
  zoom,
  selectedItemId,
  cloneIds,
  onSelect,
  ...props
}) {
  const [scale, setScale] = useState(1);
  const internalRef = useRef(null);
  const sheetRef = props.ref ?? internalRef;
  assertRefObject(sheetRef);

  // auto-update scale whenever component zoom is being changed
  useEffect(() => {
    const node = sheetRef.current;
    if (node === null || props.isPrinting) {
      return;
    }
    const nextScale = node.offsetWidth / BASE_SHEET_WIDTH;
    node.style.setProperty("--scale", `${nextScale}`);
    setScale(nextScale);
  }, [props.isPrinting, sheetRef, zoom]);

  // keep scale up-to-date when sheet is being resized
  useEffect(() => {
    const node = sheetRef.current;
    if (node === null || props.isPrinting) {
      return;
    }
    const updateScale = throttleToAnimationFrame(() => {
      const nextScale = node.offsetWidth / BASE_SHEET_WIDTH;
      node.style.setProperty("--scale", `${nextScale}`);
      setScale(nextScale);
    });
    const observer = new ResizeObserver(updateScale);
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [props.isPrinting, sheetRef]);
  return /*#__PURE__*/jsx(PrintContextProvider, {
    isPrinting: props.isPrinting ?? false,
    children: /*#__PURE__*/jsx("article", {
      ref: sheetRef,
      className: sheetCls,
      style: {
        paddingBottom: `${(page.aspectRatio * 100).toString()}%`,
        fontSize: `${(10 * scale - 0.05).toString()}px`
      },
      children: /*#__PURE__*/jsx(ScaleContext.Provider, {
        value: scale,
        children: /*#__PURE__*/jsx(TransitionGroup, {
          component: null,
          children: sortItemsByTabIndex(page).map(({
            item,
            zIndex
          }, index) => /*#__PURE__*/jsx(CSSRefTransition, {
            classNames: transitionCls,
            timeout: TRANSITION_DURATION,
            children: ref => /*#__PURE__*/jsx("div", {
              ref: ref,
              children: /*#__PURE__*/jsx(RenderComponent, {
                index: index,
                sheetRef: sheetRef,
                pageId: page.id,
                pageAspectRatio: page.aspectRatio,
                item: item,
                zIndex: zIndex,
                isSelected: item.id === selectedItemId,
                isCloned: cloneIds.some(it => it === item.id),
                onSelect: onSelect,
                ...props
              })
            })
          }, item.id))
        })
      })
    })
  });
}

// sort items within the same gridline as if they were positioned identically
const SORT_TOLERANCE = 0.01;

/**
 * sort items top-left to bottom-right to ensure consistent tab index between
 * elements, and enrich with their original zIndexes to ensure proper rendering
 * of nested layers.
 */
function sortItemsByTabIndex(page) {
  const sortedItems = page.items.slice(0).sort((a, b) => {
    if (a.position.y === b.position.y) {
      const aX = Math.round(a.position.x / SORT_TOLERANCE);
      const bX = Math.round(b.position.x / SORT_TOLERANCE);
      return aX !== bX ? aX - bX : a.position.y - b.position.y;
    }
    const aY = Math.round(a.position.y / page.aspectRatio / SORT_TOLERANCE);
    const bY = Math.round(b.position.y / page.aspectRatio / SORT_TOLERANCE);
    return aY !== bY ? aY - bY : a.position.x - b.position.x;
  });
  return sortedItems.map(item => ({
    item,
    zIndex: page.items.findIndex(it => it === item)
  }));
}

export { Sheet };
//# sourceMappingURL=Sheet.js.map
